import { assign, createMachine } from "xstate";
import { invoiceHistory } from "@nju/data-access-portal-payment";
import { BasicError } from "@nju/result";

interface IContext {
  hasInvoices: boolean | undefined;
  error?: BasicError;
  hideInvoicesOnNoEntries?: boolean;
}

type IEvent =
  | { type: "INVOICES_RECEIVED"; hasInvoices: boolean }
  | { type: "INVOICES_FETCH_ERROR"; error: BasicError };

export const dashboardPaymentSubmenuMachine = createMachine(
  {
    context: {
      hasInvoices: undefined,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "hideInvoicesOnNoEntries",
    states: {
      hideInvoicesOnNoEntries: {
        always: [
          {
            cond: "shouldFetch",
            target: "fetchInvoices",
          },
          {
            target: "noInvoices",
          },
        ],
      },
      fetchInvoices: {
        invoke: {
          src: "fetchInvoices",
        },
        on: {
          INVOICES_RECEIVED: {
            actions: "setInvoiceExist",
            target: "idle",
          },
          INVOICES_FETCH_ERROR: {
            actions: "setError",
            target: "error",
          },
        },
      },
      noInvoices: {
        type: "final",
      },
      idle: { type: "final" },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      shouldFetch: ({ hideInvoicesOnNoEntries }) => {
        return !!hideInvoicesOnNoEntries;
      },
    },
    actions: {
      setInvoiceExist: assign<IContext, IEvent>((context, event) => {
        if (event.type === "INVOICES_RECEIVED") {
          return {
            hasInvoices: event.hasInvoices,
          };
        }
        return context;
      }),
      setError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "INVOICES_FETCH_ERROR") {
          return {
            error: event.error,
            hasInvoices: false,
          };
        }
        return context;
      }),
    },
    services: {
      fetchInvoices: () => {
        return async (callback) => {
          try {
            const { invoice: invoices } = (
              await invoiceHistory({ state: "SENT", perPage: 1 })
            ).unwrap();
            const hasInvoices = invoices && invoices.length > 0;
            callback({
              type: "INVOICES_RECEIVED",
              hasInvoices,
            });
          } catch (error) {
            if (error instanceof BasicError) {
              callback({
                type: "INVOICES_FETCH_ERROR",
                error,
              });
            }
          }
        };
      },
    },
  },
);
